import { yupResolver } from '@hookform/resolvers/yup'
import EditIcon from '@mui/icons-material/Edit'
import { CircularProgress, IconButton, InputAdornment, Tooltip } from '@mui/material'
import { ActionsDialog, InputTextField } from '@src/components'
import { useUpdateCreditApplication } from '@src/data/api/credit-application/credit-application-api'
import { CreditApplication, Merchant, UnregisteredMerchantName, UnregisteredMerchantNameSchema } from '@src/data/types'
import { InternalMerchants } from '@src/data/types/constants'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface UnregisteredMerchantNameProps {
  creditApplication: CreditApplication,
  serviceProvider: string | undefined,
  merchant: Merchant
}

const UnregisteredMerchantNameInput = ({
  creditApplication,
  serviceProvider,
  merchant
}: UnregisteredMerchantNameProps) => {

  const { t } = useTranslation()
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [updateCreditApplication, isPending] = useUpdateCreditApplication()

  const handleClose = useCallback(() => {
    setOpenEditDialog(false)
  }, [])
  const handleOpen = useCallback(() => {
    setOpenEditDialog(true)
  }, [])

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<UnregisteredMerchantName>({
    mode: 'onBlur',
    defaultValues: { unregisteredMerchantName: serviceProvider },
    resolver: yupResolver(UnregisteredMerchantNameSchema),
  })

  useEffect(() => {
    reset({ unregisteredMerchantName: serviceProvider })
  }, [serviceProvider, reset])

  const handleSetUnregisteredMerchantName = useCallback(
    async (data: UnregisteredMerchantName) => {
      if (creditApplication) {
        const creditApplicationUpdate: Partial<CreditApplication> = {
          ...creditApplication,
          unregisteredMerchantName: data.unregisteredMerchantName,
        }
        await updateCreditApplication(creditApplicationUpdate)
      }
      handleClose()
    },
    [creditApplication, updateCreditApplication, handleClose],
  )

  const forcePaymentToBorrower = InternalMerchants.includes(merchant.id)

  return forcePaymentToBorrower ?
    (
      <>
        <ActionsDialog
          open={openEditDialog}
          onCancel={handleClose}
          onConfirm={handleSubmit(handleSetUnregisteredMerchantName, reportErrorToConsole)}
          disabled={!!errors.unregisteredMerchantName || isPending}
          title={t('editCreditApplication.unregisteredMerchantName.edit')} >
          <InputTextField
            autoFocus
            required
            margin="normal"
            {...register('unregisteredMerchantName')}
            error={errors.unregisteredMerchantName}
            label={t('editCreditApplication.unregisteredMerchantName.label')}
            type="text"
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end"> {isPending && <CircularProgress size={20} />} </InputAdornment>,
              },
            }}
          />
        </ActionsDialog>
        <> ({serviceProvider}
          <Tooltip title={t('editCreditApplication.unregisteredMerchantName.edit')}>
            <IconButton size="small" color="primary" onClick={handleOpen} aria-label={t('editCreditApplication.unregisteredMerchantName.edit')}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>)
        </>
      </>
    ) : <></>
}

export default UnregisteredMerchantNameInput